import React, { useState } from "react";
import {
  Box, Button, Card, Typography,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody,
  TextField, CircularProgress, Skeleton, TablePagination,
  Snackbar, Alert, InputAdornment, Tooltip, Avatar, Paper,
} from "@mui/material";
import { Plus, Pencil, Trash2, BookOpen, Tag, Hash, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetCourseCategories,
  adminCreateCourseCategory,
  adminUpdateCourseCategory,
  adminDeleteCourseCategory,
} from "../../api/Coursecategories";

const QUERY_KEY = ["admin-course-categories"];

const DEFAULT_VALUES = { label: "", order: 0 };

export default function CourseCategoriesAdminPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // Fetch categories (supports a plain array or a paginated { results: [] } response)
  const { data: apiCategories = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminGetCourseCategories().then((r) => r.data),
  });

  // Handles { success, message, data: [] }, { results: [] }, or a plain array
  const categories = Array.isArray(apiCategories)
    ? apiCategories
    : Array.isArray(apiCategories?.data)
      ? apiCategories.data
      : Array.isArray(apiCategories?.results)
        ? apiCategories.results
        : [];

  const filteredCategories = categories
    .filter((c) => c.label?.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const paginatedCategories = filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalCount = filteredCategories.length;

  const createMutation = useMutation({
    mutationFn: (data) => adminCreateCourseCategory(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      notify("Category created");
      closeDialog(true);
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create category", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateCourseCategory(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      notify("Category updated");
      closeDialog(true);
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update category", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteCourseCategory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      notify("Category deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(err?.response?.data?.message || "Couldn't delete category", "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    const payload = {
      label: values.label.trim(),
      order: Number.isFinite(values.order) ? values.order : 0,
    };
    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  const openCreate = () => {
    setEditing(null);
    // Default new categories to the end of the list
    const nextOrder = categories.length
      ? Math.max(...categories.map((c) => c.order ?? 0)) + 1
      : 0;
    reset({ ...DEFAULT_VALUES, order: nextOrder });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    reset({ label: c.label ?? "", order: c.order ?? 0 });
    setOpen(true);
  };

  const closeDialog = (force = false) => {
    if (saving && force !== true) return;
    setOpen(false);
    setEditing(null);
    reset(DEFAULT_VALUES);
  };

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  return (
    <AdminShell title="Course Categories">
      {/* Page header */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5, mb: 2.5,
      }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Manage course categories. These are used to label and group courses on the public site.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          Add Category
        </Button>
      </Box>

      {/* Search bar */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        p: 2, mb: 2.5,
      }}>
        <TextField
          fullWidth
          placeholder="Search categories by name..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#94A3B8" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              border: "1px solid rgba(15,23,42,0.15)",
              "&:hover": { borderColor: "#2563EB" },
            },
          }}
        />
      </Card>

      {/* Table card */}
      <Card sx={{
        borderRadius: "16px",
        border: "1px solid rgba(15,23,42,0.08)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        overflow: "hidden",
      }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Category", "Order", ""].map((h, i) => (
                  <TableCell key={i} sx={{
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.03em",
                    textTransform: "uppercase", color: "text.secondary",
                    borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap",
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 3 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 200 : 60} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      width: 52, height: 52, borderRadius: "14px",
                      bgcolor: "rgba(37,99,235,0.07)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 1.5,
                    }}>
                      <BookOpen size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      No categories found
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search" : "Add your first category to organize courses."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedCategories.map((c) => (
                <TableRow key={c.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 38, height: 38, borderRadius: "9px",
                          bgcolor: "rgba(37,99,235,0.1)", color: "#2563EB", flexShrink: 0,
                        }}
                      >
                        <BookOpen size={16} />
                      </Avatar>
                      <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                        {c.label}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ fontSize: 13.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {c.order ?? "—"}
                  </TableCell>

                  <TableCell align="right">
                    <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(c)}
                          sx={{
                            border: "1px solid rgba(15,23,42,0.1)",
                            borderRadius: "8px",
                            "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" },
                          }}
                        >
                          <Pencil size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setConfirmDelete(c)}
                          sx={{
                            border: "1px solid rgba(15,23,42,0.1)",
                            borderRadius: "8px",
                            "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" },
                          }}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid rgba(15,23,42,0.08)",
            "& .MuiTablePagination-toolbar": { padding: "16px 24px" },
            "& .MuiTablePagination-selectLabel": { fontSize: "13.5px", color: "text.secondary", marginRight: "8px" },
            "& .MuiTablePagination-displayedRows": { fontSize: "13.5px", color: "text.secondary" },
            "& .MuiInputBase-root": { borderRadius: "8px", border: "1px solid rgba(15,23,42,0.15)" },
            "& .MuiTablePagination-actions": { gap: "4px" },
            "& .MuiIconButton-root": {
              borderRadius: "8px", border: "1px solid rgba(15,23,42,0.1)", width: "32px", height: "32px",
            },
          }}
        />
      </Card>

      {/* Create / Edit dialog */}
      <Dialog
        open={open}
        onClose={() => closeDialog()}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow: "0 30px 70px rgba(15,23,42,0.22)",
          },
        }}
      >
        <DialogTitle sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: 2.5, px: 3,
          background: "linear-gradient(135deg, #7C3AED 0%, #2563EB 55%, #1D4ED8 100%)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <BookOpen size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF", letterSpacing: "-0.01em" }}>
                {editing ? "Edit Category" : "Add New Category"}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", fontWeight: 500, mt: 0.25 }}>
                {editing ? `Editing: ${editing.label}` : "Create a new course category"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{
            display: "flex", flexDirection: "column", gap: 2.5,
            pt: 3, pb: 1, px: 3,
            maxHeight: "65vh", overflowY: "auto",
            backgroundColor: "#F1F5F9",
          }}>
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #7C3AED",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: "9px",
                  backgroundColor: "rgba(124,58,237,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Tag size={17} color="#7C3AED" />
                </Box>
                <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>
                  Category Details
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Category Name"
                    fullWidth
                    required
                    disabled={saving}
                    error={!!errors.label}
                    helperText={errors.label ? "Category name is required" : ""}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Tag size={16} color="#94A3B8" /></InputAdornment>,
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("label", {
                      required: true,
                      validate: (v) => v.trim().length > 0,
                    })}
                  />
                </Box>

                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Display Order"
                    type="number"
                    fullWidth
                    disabled={saving}
                    placeholder="0 = first"
                    helperText="Lower numbers appear first."
                    inputProps={{ step: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Hash size={16} color="#94A3B8" /></InputAdornment>,
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("order", { valueAsNumber: true })}
                  />
                </Box>
              </Box>
            </Paper>
          </DialogContent>

          <DialogActions sx={{
            px: 3, py: 2.25,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            backgroundColor: "#FFFFFF",
            display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5,
          }}>
            <Button
              onClick={() => closeDialog()}
              disabled={saving}
              sx={{
                textTransform: "none", fontWeight: 700, color: "#475569",
                px: 2.5, py: 1, borderRadius: "10px",
                backgroundColor: "rgba(15,23,42,0.04)",
                "&:hover": { backgroundColor: "rgba(15,23,42,0.08)" },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                background: "linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)",
                px: 3.5, py: 1.1, minWidth: 140,
                boxShadow: "0 4px 14px rgba(124,58,237,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #6D28D9 0%, #1D4ED8 100%)",
                  boxShadow: "0 6px 18px rgba(124,58,237,0.4)",
                },
              }}
            >
              {saving ? "Saving..." : editing ? "Update Category" : "Create Category"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: "1px solid rgba(15,23,42,0.08)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this category?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.label}" will be permanently removed. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: "1px solid rgba(15,23,42,0.08)" }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "10px",
              bgcolor: "#EF4444", px: 2.5, minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" },
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}